import React, { useEffect, useRef, useState } from "react";
import { Award, Printer, X } from "lucide-react";
import { fontCss } from "../lib/beaconStyles";
import { levelLabel } from "../lib/learn";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { CertificateEditor } from "./CertificateEditor";

const FONT_LINKS = [
  "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=Fraunces:opsz,wght@9..144,500;9..144,700&family=DM+Serif+Display&family=Space+Grotesk:wght@500;700&family=Poppins:wght@500;600;700&family=Cormorant+Garamond:ital,wght@0,500;1,500&family=Great+Vibes&display=swap",
  "https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@500,700,800&f[]=satoshi@400,500,700&display=swap",
];
const PAPER = { light: "#FFFFFF", linen: "#F7F1E6", sage: "#F4F7F1", taupe: "#F6F1EA", charcoal: "#FBF8F2", navy: "#FBF8F2", purple: "#FBF8FE", teal: "#F3FBFA", dark: "#FBF8F2" };
const esc = (s) => String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export const CERT_DEFAULTS = { heading: "Certificate of Completion", intro: "This certifies that", completed: "has successfully completed the course", note: "", signer_name: "", signer_title: "Teacher", signature_style: "script", logo: "" };
const SCRIPT = "'Great Vibes', 'Brush Script MT', cursive";

// One certificate page (11 x 8.5 in), used for both the on-screen preview and the printable PDF.
export const certificateHtml = ({ name, course, lessons, level, estTime, teacher, beaconTitle, accent, headingFont, theme, date, cert = {}, logoSrc }) => {
  const c = { ...CERT_DEFAULTS, ...Object.fromEntries(Object.entries(cert).filter(([k, v]) => v || k === "note")) };
  const signer = c.signer_name || teacher;
  const sigFont = c.signature_style === "script" ? `font-family:${SCRIPT};font-style:normal;font-size:38px;line-height:1` : "";
  const head = fontCss(headingFont);
  const paper = PAPER[theme] || PAPER.dark;
  const details = [`${lessons} lesson${lessons === 1 ? "" : "s"}`, level && levelLabel(level), estTime].filter(Boolean).map(esc).join("&nbsp;&nbsp;·&nbsp;&nbsp;");
  const seal = esc((beaconTitle || teacher || "S").trim().charAt(0).toUpperCase());
  return `<!doctype html><html><head><meta charset="utf-8"><title>Certificate: ${esc(course)}</title>
${FONT_LINKS.map((h) => `<link rel="stylesheet" href="${h}">`).join("")}
<style>
@page{size:11in 8.5in;margin:0}*{box-sizing:border-box}html,body{margin:0;padding:0;background:${paper}}
body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
.page{width:11in;height:8.5in;padding:.45in;background:${paper};color:#1C1B19;font-family:'Cormorant Garamond',Georgia,serif}
.frame{height:100%;border:2px solid ${accent};padding:10px}
.inner{height:100%;border:1px solid ${accent}66;display:flex;flex-direction:column;align-items:center;justify-content:space-between;padding:.5in .8in .4in;text-align:center}
.seal{width:64px;height:64px;margin:0 auto;border-radius:50%;border:2px solid ${accent};display:flex;align-items:center;justify-content:center;font-family:${head};font-size:28px;color:${accent};box-shadow:0 0 0 5px ${paper},0 0 0 6px ${accent}55;overflow:hidden;background:${paper}}
.seal.logo{width:88px;height:88px}.seal img{width:100%;height:100%;object-fit:cover}
.kicker{font-family:'Satoshi',sans-serif;font-size:13px;letter-spacing:.42em;text-transform:uppercase;color:${accent};margin-top:18px}
.lead{font-style:italic;font-size:20px;color:#5B5750;margin:0}
.name{font-family:${head};font-size:58px;line-height:1.1;margin:8px 0 0;color:#1C1B19;word-break:break-word}
.rule{width:3.6in;height:1px;background:${accent};margin:14px auto 16px}
.course{font-family:${head};font-size:30px;line-height:1.2;color:${accent};margin:6px 0 0}
.details{font-family:'Satoshi',sans-serif;font-size:12px;letter-spacing:.08em;color:#6E6A62;margin-top:10px}
.foot{width:100%;display:flex;justify-content:space-between;align-items:flex-end}
.col{width:2.6in;text-align:center}.sig{font-family:${head};font-size:22px;font-style:italic;color:#1C1B19;min-height:28px}
.line{height:1px;background:#1C1B1955;margin:6px 0}.lbl{font-family:'Satoshi',sans-serif;font-size:10px;letter-spacing:.25em;text-transform:uppercase;color:#77736C}
.mid{font-size:15px;font-style:italic;color:#5B5750;max-width:3in}
.tiny{font-family:'Satoshi',sans-serif;font-size:9px;letter-spacing:.2em;text-transform:uppercase;color:#9A958C;margin-top:14px}
.note{font-style:italic;font-size:17px;line-height:1.4;color:#4A463F;max-width:6.4in;margin:16px auto 0;word-break:break-word}
</style></head><body><div class="page"><div class="frame"><div class="inner">
<div><div class="seal${logoSrc ? " logo" : ""}">${logoSrc ? `<img src="${esc(logoSrc)}" alt="">` : seal}</div><div class="kicker">${esc(c.heading)}</div></div>
<div><p class="lead">${esc(c.intro)}</p><div class="name">${esc(name) || "&nbsp;"}</div><div class="rule"></div>
<p class="lead">${esc(c.completed)}</p><div class="course">${esc(course)}</div><div class="details">${details}</div>
${c.note ? `<p class="note">&ldquo;${esc(c.note)}&rdquo;</p>` : ""}</div>
<div style="width:100%"><div class="foot">
<div class="col"><div class="sig" style="font-style:normal;font-family:'Cormorant Garamond',Georgia,serif;font-size:20px">${esc(date)}</div><div class="line"></div><div class="lbl">Date awarded</div></div>
<div class="mid">Offered through<br><span style="font-family:${head};font-style:normal;color:${accent}">${esc(beaconTitle)}</span></div>
${c.signature_style === "none" ? `<div class="col"></div>` : `<div class="col"><div class="sig" style="${sigFont}">${esc(signer)}</div><div class="line"></div><div class="lbl">${esc(c.signer_title)}</div></div>`}
</div><div class="tiny">Issued privately on Sovereign Signal</div></div>
</div></div></div></body></html>`;
};

// Loads the creator's seal logo as a data URL so it works inside the preview frame and the print window.
export const useLogoSrc = (path) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let live = true;
    setSrc(null);
    if (!path) return undefined;
    api.get(`/files/${path}`, { responseType: "blob" }).then((r) => {
      const fr = new FileReader();
      fr.onload = () => live && setSrc(fr.result);
      fr.readAsDataURL(r.data);
    }).catch(() => {});
    return () => { live = false; };
  }, [path]);
  return src;
};

export const certData = (b, section, name, logoSrc) => ({ logoSrc,
  name, course: section.title, lessons: section.items.filter((it) => it.type !== "artwork").length, level: section.level, estTime: section.est_time,
  teacher: b.creator_name || "", beaconTitle: b.title, accent: b.accent_color || "#14A89A", headingFont: b.heading_font, theme: b.page_theme, cert: b.certificate || {},
  date: new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }),
});

// Scaled live preview of the certificate page.
export const CertificatePreview = ({ html }) => {
  const box = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    if (box.current) ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  const scale = w / 1056;
  return (
    <div ref={box} className="w-full overflow-hidden border border-white/10" style={{ height: 816 * scale }} data-testid="certificate-preview">
      {w > 0 && <iframe title="Certificate preview" srcDoc={html} style={{ width: 1056, maxWidth: "none", height: 816, border: 0, transform: `scale(${scale})`, transformOrigin: "0 0" }} />}
    </div>
  );
};

const printCert = (html) => {
  const win = window.open("", "_blank");
  if (!win) return false;
  win.document.write(html); win.document.close();
  const go = () => setTimeout(() => win.print(), 200);
  if (win.document.fonts?.ready) win.document.fonts.ready.then(go); else go();
  return true;
};

// Shown when a student finishes every lesson in a course. Their name is typed here and never sent anywhere.
export const CertificateButton = ({ beacon, section, accent }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [msg, setMsg] = useState("");
  const logoSrc = useLogoSrc(beacon?.certificate?.logo);
  if (!beacon) return null;
  const html = certificateHtml(certData(beacon, section, name.trim(), logoSrc));
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`certificate-open-${section.id}`} className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 font-body text-xs font-semibold" style={{ background: accent, color: "#0A0A0A" }}>
        <Award size={13} /> Get your certificate
      </button>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => setOpen(false)} data-testid="certificate-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#121212] border border-white/10 w-full max-w-3xl max-h-[92vh] overflow-y-auto p-5 relative">
            <button onClick={() => setOpen(false)} aria-label="Close" data-testid="certificate-close" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={18} /></button>
            <h3 className="font-body text-lg text-white flex items-center gap-2 mb-1"><Award size={17} style={{ color: accent }} /> Your certificate</h3>
            <p className="font-body text-xs text-zinc-500 mb-3">Beautifully done. Type your name as you'd like it to appear. It's made on your device and never saved or sent anywhere.</p>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Your name" data-testid="certificate-name"
              className="w-full mb-3 bg-[#0A0A0A] border border-white/10 px-3 py-2 font-body text-sm text-white outline-none focus:border-white/30" />
            <CertificatePreview html={html} />
            <div className="flex items-center gap-3 mt-3">
              <button type="button" disabled={!name.trim()} onClick={() => setMsg(printCert(html) ? "" : "Please allow pop ups to save your certificate.")} data-testid="certificate-download"
                className="inline-flex items-center gap-1.5 px-4 py-2 font-body text-sm font-semibold disabled:opacity-40" style={{ background: accent, color: "#0A0A0A" }}>
                <Printer size={14} /> Save as PDF
              </button>
              <span className="font-body text-[11px] text-zinc-500">In the print window, choose Save as PDF. Landscape, letter size.</span>
            </div>
            {msg && <p className="font-body text-xs text-amber-400/90 mt-2" data-testid="certificate-msg">{msg}</p>}
          </div>
        </div>
      )}
    </>
  );
};

// Teacher setting: turn certificates on or off, and preview exactly what students receive.
export const CertificateSetting = ({ beacon, onChange, Switch }) => {
  const on = !!beacon.certificates_enabled;
  const [show, setShow] = useState(false);
  const course = (beacon.sections || []).find((s) => s.items?.some((it) => it.type !== "artwork")) || { title: "Your course title", items: [], level: "", est_time: "" };
  return (
    <div className="mt-4 pt-4 border-t border-white/10" data-testid="certificate-setting">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="font-body text-sm text-white">Completion certificates</div>
          <p className="font-body text-xs text-zinc-500 mt-0.5">{on ? "On" : "Off"}. When a student completes every lesson in a course, they can download a certificate in your Beacon's fonts and colours, with the name they type. Needs Progress tracking on. Names are never saved.</p>
          <button type="button" onClick={() => setShow((v) => !v)} data-testid="certificate-preview-toggle" className="mt-1.5 font-body text-xs text-[#14A89A] hover:underline">{show ? "Close" : "Edit and preview the certificate"}</button>
          {on && !show && !beacon.certificate?.signer_name && !beacon.creator_name && beacon.certificate?.signature_style !== "none" && <p className="font-body text-[11px] text-amber-300/90 mt-1" data-testid="certificate-signer-hint">Add the name you'd like to sign with, so the signature line isn't blank.</p>}
        </div>
        <Switch on={on} field="certificates_enabled" testid="certificates-toggle" onChange={onChange} />
      </div>
      {show && <CertificateEditor beacon={beacon} course={course} onSaved={onChange} />}
    </div>
  );
};

